// EMERGENCY FIX - CreateGameModal with INLINE BUTTON STYLES
// This bypasses any CSS import/caching issues

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import socket from './socket';
import './CreateGameModal.css';

const PUCK_COLORS = [
  '#ef4444', '#3b82f6', '#22c55e', '#a855f7', '#f97316',
  '#eab308', '#06b6d4', '#ec4899', '#f59e0b', '#84cc16',
  '#8b5cf6', '#06d6a0', '#f43f5e', '#0ea5e9', '#d946ef',
  '#65a30d', '#dc2626', '#1d4ed8', '#059669', '#7c2d12',
  '#374151', '#fbbf24', '#fb7185', '#34d399'
];

const AI_DIFFICULTY_LEVELS = [
  'Basic', 'iThinker', 'Angry Teenager', 'PhD Scientist', 'Insane'
];

export default function CreateGameModal({ onClose, isGuestMode = false }) {
  const navigate = useNavigate();
  const [players, setPlayers] = useState(2);
  const [pucks, setPucks] = useState(3);
  const [privacy, setPrivacy] = useState('public');
  const [type, setType] = useState('casual');
  
  const [playerConfigs, setPlayerConfigs] = useState([
    { name: 'Player 1', isAI: false, color: PUCK_COLORS[0], aiDifficulty: 'Basic' },
    { name: 'Player 2', isAI: false, color: PUCK_COLORS[1], aiDifficulty: 'Basic' }
  ]);

  const handlePlayersChange = (newPlayerCount) => {
    setPlayers(newPlayerCount);
    const newConfigs = [...playerConfigs];
    
    while (newConfigs.length < newPlayerCount) {
      const playerNum = newConfigs.length + 1;
      newConfigs.push({
        name: `Player ${playerNum}`,
        isAI: false,
        color: PUCK_COLORS[newConfigs.length % PUCK_COLORS.length],
        aiDifficulty: 'Basic'
      });
    }
    
    if (newConfigs.length > newPlayerCount) {
      newConfigs.splice(newPlayerCount);
    }
    
    setPlayerConfigs(newConfigs);
  };

  const updatePlayerConfig = (index, field, value) => {
    const newConfigs = [...playerConfigs];
    newConfigs[index] = { ...newConfigs[index], [field]: value };
    setPlayerConfigs(newConfigs);
  };

  const handleCreateGame = (e) => {
    e.preventDefault();
    
    if (isGuestMode) {
      const gameConfig = {
        players: playerConfigs,
        pucksPerTurn: pucks,
        gameType: type,
        isGuestMode: true
      };
      
      sessionStorage.setItem('gameConfig', JSON.stringify(gameConfig));
      navigate('/game');
      onClose();
    } else {
      const gameOptions = { 
        players, 
        pucksPerTurn: pucks, 
        privacy, 
        gameType: type
      };
      
      console.log('🎯 Creating online game with options:', gameOptions);
      
      sessionStorage.setItem('gameConfig', JSON.stringify({
        gameType: type,
        pucksPerTurn: pucks,
        players: players
      }));
      
      socket.emit('createGame', gameOptions);
      onClose();
    }
  };

  const isConfigValid = () => {
    return playerConfigs.every(config => config.name.trim() !== '');
  };

  // INLINE BUTTON STYLES - Guaranteed to work
  const buttonBaseStyle = {
    padding: '0.75rem 1.5rem',
    borderRadius: '0.5rem',
    fontWeight: '600',
    fontSize: '1rem',
    cursor: 'pointer',
    border: 'none',
    transition: 'all 0.3s ease',
    minWidth: '100px',
    textAlign: 'center',
    whiteSpace: 'nowrap'
  };

  const primaryButtonStyle = {
    ...buttonBaseStyle,
    background: 'linear-gradient(45deg, #2563eb, #7c3aed)',
    color: 'white'
  };

  const secondaryButtonStyle = {
    ...buttonBaseStyle,
    color: '#60a5fa',
    background: 'rgba(96, 165, 250, 0.1)',
    border: '2px solid #3b82f6'
  };

  const disabledButtonStyle = {
    ...primaryButtonStyle,
    background: '#374151',
    color: '#6b7280',
    cursor: 'not-allowed'
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-content" style={{ maxWidth: '600px', maxHeight: '80vh', overflowY: 'auto' }}>
        <h2>{isGuestMode ? 'Setup Guest Game' : 'Create New Game'}</h2>
        <form onSubmit={handleCreateGame}>
          <label>Players:</label>
          <select value={players} onChange={(e) => handlePlayersChange(Number(e.target.value))}>
            <option value={2}>2 Players</option>
            <option value={3}>3 Players</option>
            <option value={4}>4 Players</option>
          </select>

          <label>Pucks per Turn:</label>
          <select value={pucks} onChange={(e) => setPucks(Number(e.target.value))}>
            <option value={3}>3 Pucks</option>
            <option value={5}>5 Pucks</option>
            <option value={7}>7 Pucks</option>
          </select>

          {!isGuestMode && (
            <>
              <label>Game Type:</label>
              <select value={type} onChange={(e) => setType(e.target.value)}>
                <option value="casual">🎮 Casual - Fun play, no rank changes</option>
                <option value="ranked">🏆 Ranked - Competitive with rank points</option>
              </select>

              <label>Privacy:</label>
              <select value={privacy} onChange={(e) => setPrivacy(e.target.value)}>
                <option value="public">Public - Anyone can join</option>
                <option value="private">Private - Invite only</option>
              </select>
            </>
          )}

          {isGuestMode && (
            <>
              <label>Game Type:</label>
              <select value={type} onChange={(e) => setType(e.target.value)}>
                <option value="casual">🎮 Casual - Fun play</option>
                <option value="ranked">🏆 Ranked - Competitive (requires login)</option>
              </select>

              <div className="player-configs">
                <h3>Player Setup</h3>
                {playerConfigs.map((config, index) => (
                  <div key={index} className="player-config" style={{ 
                    border: '1px solid #374151', 
                    borderRadius: '8px', 
                    padding: '1rem', 
                    marginBottom: '1rem',
                    backgroundColor: '#1f2937'
                  }}>
                    <h4>Player {index + 1}</h4>
                    
                    <div style={{ marginBottom: '0.5rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="checkbox"
                          checked={config.isAI}
                          onChange={(e) => updatePlayerConfig(index, 'isAI', e.target.checked)}
                        />
                        AI Player
                      </label>
                    </div>

                    <div style={{ marginBottom: '0.5rem' }}>
                      <label>Name:</label>
                      <input
                        type="text"
                        value={config.name}
                        onChange={(e) => updatePlayerConfig(index, 'name', e.target.value)}
                        placeholder={`Player ${index + 1}`}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          backgroundColor: '#374151',
                          border: '1px solid #4b5563',
                          borderRadius: '4px',
                          color: 'white'
                        }}
                      />
                    </div>

                    <div style={{ marginBottom: '0.5rem' }}>
                      <label>Color:</label>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
                        {PUCK_COLORS.map((color) => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => updatePlayerConfig(index, 'color', color)}
                            style={{
                              width: '30px',
                              height: '30px',
                              backgroundColor: color,
                              border: config.color === color ? '3px solid white' : '1px solid #4b5563',
                              borderRadius: '50%',
                              cursor: 'pointer'
                            }}
                          />
                        ))}
                      </div>
                    </div>

                    {config.isAI && (
                      <div>
                        <label>AI Difficulty:</label>
                        <select
                          value={config.aiDifficulty}
                          onChange={(e) => updatePlayerConfig(index, 'aiDifficulty', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            backgroundColor: '#374151',
                            border: '1px solid #4b5563',
                            borderRadius: '4px',
                            color: 'white'
                          }}
                        >
                          {AI_DIFFICULTY_LEVELS.map((level) => (
                            <option key={level} value={level}>{level}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* INLINE STYLED BUTTONS - Guaranteed to work! */}
          <div className="modal-actions">
            <button 
              type="button" 
              onClick={onClose}
              style={secondaryButtonStyle}
              onMouseEnter={(e) => {
                e.target.style.background = '#3b82f6';
                e.target.style.color = 'white';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 15px rgba(59, 130, 246, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(96, 165, 250, 0.1)';
                e.target.style.color = '#60a5fa';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={isGuestMode && !isConfigValid()}
              style={
                (isGuestMode && !isConfigValid()) 
                  ? disabledButtonStyle 
                  : primaryButtonStyle
              }
              onMouseEnter={(e) => {
                if (!(isGuestMode && !isConfigValid())) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.5)';
                }
              }}
              onMouseLeave={(e) => {
                if (!(isGuestMode && !isConfigValid())) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }
              }}
            >
              {isGuestMode ? 'Start Game' : 'Create Game'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
